import React, { useState, useEffect } from 'react';
import { getAnalysisStatus } from '../../api/client';
import { SceneState, useSceneStore, ColorScale } from '../../state/sceneStore';

import { formatInputNumber } from '../../utils/formatting';
import { getColorFromScale } from '../../utils/colorScale';
import { exportSceneImage } from '../../utils/exportImage';

const generateGradient = (colorScale: ColorScale): string => {
  const steps = 20;
  const colors = [];
  for (let i = 0; i <= steps; i++) {
    const normalized = i / steps;
    const { r, g, b } = getColorFromScale(normalized, colorScale);
    colors.push(`rgb(${r}, ${g}, ${b})`);
  }
  return `linear-gradient(to right, ${colors.join(', ')})`;
};

const SimulationPanel: React.FC = () => {
  const sensors = useSceneStore((state: SceneState) => state.sensors);

  const setHeatmapData = useSceneStore((state: SceneState) => state.setHeatmapData);
  const setHeatmapVisible = useSceneStore((state: SceneState) => state.setHeatmapVisible);
  const heatmapVisible = useSceneStore((state: SceneState) => state.heatmapVisible);
  const heatmapData = useSceneStore((state: SceneState) => state.heatmapData);
  const heatmapTaskId = useSceneStore((state: SceneState) => state.heatmapTaskId);
  const setHeatmapTaskId = useSceneStore((state: SceneState) => state.setHeatmapTaskId);
  const startHeatmapGeneration = useSceneStore((state: SceneState) => state.startHeatmapGeneration);
  const cancelHeatmapGeneration = useSceneStore((state: SceneState) => state.cancelHeatmapGeneration);
  const sceneOptions = useSceneStore((state: SceneState) => state.sceneOptions);
  const setSceneOptions = useSceneStore((state: SceneState) => state.setSceneOptions);
  const isWallDrawingMode = useSceneStore((state: SceneState) => state.isWallDrawingMode);
  const sceneCanvas = useSceneStore((state: SceneState) => state.sceneCanvas);
  const sceneCamera = useSceneStore((state: SceneState) => state.sceneCamera);
  const useOrthographic = useSceneStore((state: SceneState) => state.useOrthographic);
  const currentProject = useSceneStore((state: SceneState) => state.currentProject);
  const [showColorScaleDropdown, setShowColorScaleDropdown] = useState(false);
  const [progress, setProgress] = useState(0);
  const [statusMessage, setStatusMessage] = useState('Starting...');
  const [isExporting, setIsExporting] = useState(false);

  const isLoading = !!heatmapTaskId;
  const canExport = !!sceneCanvas && !!sceneCamera;

  const handleExportImage = async () => {
    if (!sceneCanvas || !sceneCamera) return;
    setIsExporting(true);
    try {
      await exportSceneImage({
        sceneCanvas,
        camera: sceneCamera,
        useOrthographic,
        projectName: currentProject?.name,
        targetConfig: sceneOptions.targetConfig,
        heatmap: heatmapVisible && heatmapData
          ? {
            visible: true,
            min: sceneOptions.heatmapScale.min,
            max: sceneOptions.heatmapScale.max,
            colorScale: sceneOptions.colorScale,
          }
          : undefined,
      });
    } catch (error) {
      console.error('Failed to export image:', error);
      alert('Failed to export image. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };


  // Use targetConfig from sceneOptions instead of local state
  const targetConfig = sceneOptions.targetConfig;

  // Validation functions for grid parameters
  const validateAreaOfInterest = (areaOfInterest: { min_x: number; max_x: number; min_y: number; max_y: number }) => {
    const errors: string[] = [];

    if (areaOfInterest.min_x >= areaOfInterest.max_x) {
      errors.push('Min X must be less than Max X');
    }

    if (areaOfInterest.min_y >= areaOfInterest.max_y) {
      errors.push('Min Y must be less than Max Y');
    }

    return errors;
  };

  const isValidAreaOfInterest = (areaOfInterest: { min_x: number; max_x: number; min_y: number; max_y: number }) => {
    return validateAreaOfInterest(areaOfInterest).length === 0;
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (showColorScaleDropdown && !target.closest('.color-scale-dropdown')) {
        setShowColorScaleDropdown(false);
      }
    };

    if (showColorScaleDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showColorScaleDropdown]);

  useEffect(() => {
    if (!heatmapTaskId) return;

    const interval = setInterval(async () => {
      try {
        const statusResult = await getAnalysisStatus(heatmapTaskId);

        // Update progress information
        if (statusResult.progress !== undefined) {
          setProgress(statusResult.progress);
        }
        if (statusResult.status_message) {
          setStatusMessage(statusResult.status_message);
        }

        if (statusResult.status === 'SUCCESS' && statusResult.result) {
          const data = statusResult.result;

          if (
            data
            && typeof data === 'object'
            && !Array.isArray(data)
            && 'error' in data
            && (data as { error?: string }).error === 'Task was cancelled'
          ) {
            setHeatmapTaskId(null);
            setProgress(0);
            setStatusMessage('');
            clearInterval(interval);
            return;
          }

          // Ensure data is in the correct format (2D array)
          if (Array.isArray(data) && data.length > 0 && Array.isArray(data[0])) {
            setHeatmapData(data);
            setHeatmapVisible(true);
            setHeatmapTaskId(null);
            setProgress(100);

            setStatusMessage('Complete!');
            clearInterval(interval);

            let min = Infinity;
            let max = -Infinity;
            data.forEach(row => {
              if (Array.isArray(row)) {
                row.forEach(val => {
                  if (typeof val === 'number' && !isNaN(val)) {
                    if (val < min) min = val;
                    if (val > max) max = val;
                  }
                });
              }
            });
            setSceneOptions({ heatmapScale: { min, max } });
          } else if (data && typeof data === 'object' && !Array.isArray(data) && 'streaming' in data && data.streaming) {
            // Handle streaming/large dataset response
            setStatusMessage('Loading large dataset...');
            try {
              const currentProject = useSceneStore.getState().currentProject;
              if (currentProject) {
                const { loadCachedHeatmap } = await import('../../api/client');
                const cacheKey = data.cache_key;
                const gridWidth = data.grid_width;
                const gridHeight = data.grid_height;
                const estimatedSizeMb = data.estimated_size_mb || 10; // Default to 10MB if not provided
                const estimatedSizeBytes = estimatedSizeMb * 1024 * 1024;
                
                // Clear interval immediately so we don't start multiple parallel downloads
                clearInterval(interval);
                
                const cachedData = await loadCachedHeatmap(currentProject.id, cacheKey, gridWidth, gridHeight, (loaded, total) => {
                  if (total) {
                    setProgress(Math.round((loaded * 100) / total));
                  } else {
                    // Fallback to estimated size if total is unknown (due to chunked streaming)
                    // Cap at 99% until fully loaded
                    const percent = Math.min(99, Math.round((loaded * 100) / estimatedSizeBytes));
                    setProgress(percent);
                  }
                  setStatusMessage(`Downloading data... ${(loaded / 1024 / 1024).toFixed(1)}MB`);
                });
                
                if (cachedData && cachedData.heatmap) {
                  setHeatmapData(cachedData.heatmap);
                  setHeatmapVisible(true);
                  setHeatmapTaskId(null);
                  setProgress(100);
                  setStatusMessage('Complete!');
                  
                  let min = Infinity;
                  let max = -Infinity;
                  cachedData.heatmap.forEach(row => {
                    if (Array.isArray(row)) {
                      row.forEach(val => {
                        if (typeof val === 'number' && !isNaN(val)) {
                          if (val < min) min = val;
                          if (val > max) max = val;
                        }
                      });
                    }
                  });
                  setSceneOptions({ heatmapScale: { min, max } });
                } else {
                  throw new Error('Invalid cache data');
                }
              } else {
                throw new Error('No current project');
              }
            } catch (err) {
              console.error('Error loading large heatmap:', err);
              setHeatmapTaskId(null);
              setProgress(0);
              setStatusMessage('Error loading large data');
              alert('Failed to load large heatmap data.');
            }
          } else {
            console.error('Invalid heatmap data format:', data);
            setHeatmapTaskId(null);
            setProgress(0);

            setStatusMessage('Invalid data format');
            clearInterval(interval);
            alert('Received invalid heatmap data format');
          }
        } else if (statusResult.status === 'FAILURE') {
          setHeatmapTaskId(null);
          setProgress(0);

          const serverMessage = statusResult.status_message || 'Unknown error';
          setStatusMessage('Failed');
          clearInterval(interval);
          alert(`Heatmap generation failed: ${serverMessage}`);
        }
      } catch (error) {
        console.error('Error fetching heatmap status:', error);
        setHeatmapTaskId(null);
        setProgress(0);

        setStatusMessage('Error');
        clearInterval(interval);
        alert('Failed to get heatmap status.');
      }
    }, 2000); // Poll every 2 seconds

    return () => clearInterval(interval);
  }, [heatmapTaskId, setHeatmapData, setSceneOptions, setHeatmapVisible, setHeatmapTaskId]);


  const handleGenerateHeatmap = async () => {
    // Validate that sensors exist before generating heatmap
    if (!sensors || sensors.length === 0) {
      alert('Please add at least one sensor before generating a heatmap.');
      return;
    }

    // Validate grid parameters
    const gridErrors = validateAreaOfInterest(sceneOptions.areaOfInterest);
    if (gridErrors.length > 0) {
      alert(`Invalid grid parameters:\n${gridErrors.join('\n')}`);
      return;
    }

    try {
      const targetParams = {
        width: targetConfig.width,
        height: targetConfig.height,
        depth: targetConfig.depth,
        shape: targetConfig.shape,
        ground_offset: targetConfig.groundOffset
      };

      const gridParams = {
        ...sceneOptions.areaOfInterest
      };

      await startHeatmapGeneration(targetParams, gridParams);
    } catch (error) {
      console.error('Error generating heatmap:', error);
      alert('Failed to generate heatmap.');
    }
  };

  const handleCancel = async () => {
    await cancelHeatmapGeneration();
  };

  const panelStyle: React.CSSProperties = {
    background: 'rgba(25, 35, 45, 0.8)',
    color: 'white',
    padding: '15px',
    borderRadius: '8px',
    flex: '0 0 auto',
    minHeight: 0,
  };

  const inputGroupStyle: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: 'auto 1fr',
    gap: '5px 10px',
    alignItems: 'center',
    marginBottom: '10px',
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    boxSizing: 'border-box',
    background: '#334155',
    color: 'white',
    border: '1px solid #475569',
    borderRadius: '4px',
    padding: '4px 8px',
  };

  const buttonStyle: React.CSSProperties = {
    background: '#4f46e5',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    padding: '8px 12px',
    cursor: 'pointer',
    width: '100%',
  };

  return (
    <div style={panelStyle}>
      <h4>Simulation</h4>

      {/* Only show simulation controls in design mode */}
      {!isWallDrawingMode && (
        <>

          {/* Area of Interest */}
          <div style={{ marginBottom: '20px' }}>
            <h5 style={{ marginTop: 0, marginBottom: '10px' }}>Area of Interest</h5>
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              <div style={{ flex: '1 1 45%' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontSize: '12px' }}>Min X:</label>
                <input
                  type="number"
                  step={0.001}
                  value={formatInputNumber(sceneOptions.areaOfInterest.min_x)}
                  onChange={(e) => setSceneOptions(opts => ({ areaOfInterest: { ...opts.areaOfInterest, min_x: Number(e.target.value) } }))}
                  style={{
                    ...inputStyle,
                    fontSize: '12px',
                    padding: '4px 6px',
                    borderColor: sceneOptions.areaOfInterest.min_x >= sceneOptions.areaOfInterest.max_x ? '#ef4444' : undefined
                  }}
                />
              </div>
              <div style={{ flex: '1 1 45%' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontSize: '12px' }}>Max X:</label>
                <input
                  type="number"
                  step={0.001}
                  value={formatInputNumber(sceneOptions.areaOfInterest.max_x)}
                  onChange={(e) => setSceneOptions(opts => ({ areaOfInterest: { ...opts.areaOfInterest, max_x: Number(e.target.value) } }))}
                  style={{
                    ...inputStyle,
                    fontSize: '12px',
                    padding: '4px 6px',
                    borderColor: sceneOptions.areaOfInterest.min_x >= sceneOptions.areaOfInterest.max_x ? '#ef4444' : undefined
                  }}
                />
              </div>
              <div style={{ flex: '1 1 45%' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontSize: '12px' }}>Min Y:</label>
                <input
                  type="number"
                  step={0.001}
                  value={formatInputNumber(sceneOptions.areaOfInterest.min_y)}
                  onChange={(e) => setSceneOptions(opts => ({ areaOfInterest: { ...opts.areaOfInterest, min_y: Number(e.target.value) } }))}
                  style={{
                    ...inputStyle,
                    fontSize: '12px',
                    padding: '4px 6px',
                    borderColor: sceneOptions.areaOfInterest.min_y >= sceneOptions.areaOfInterest.max_y ? '#ef4444' : undefined
                  }}
                />
              </div>
              <div style={{ flex: '1 1 45%' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontSize: '12px' }}>Max Y:</label>
                <input
                  type="number"
                  step={0.001}
                  value={formatInputNumber(sceneOptions.areaOfInterest.max_y)}
                  onChange={(e) => setSceneOptions(opts => ({ areaOfInterest: { ...opts.areaOfInterest, max_y: Number(e.target.value) } }))}
                  style={{
                    ...inputStyle,
                    fontSize: '12px',
                    padding: '4px 6px',
                    borderColor: sceneOptions.areaOfInterest.min_y >= sceneOptions.areaOfInterest.max_y ? '#ef4444' : undefined
                  }}
                />
              </div>
            </div>

            {/* Show validation errors */}
            {!isValidAreaOfInterest(sceneOptions.areaOfInterest) && (
              <div style={{
                marginTop: '8px',
                padding: '8px',
                backgroundColor: 'rgba(239, 68, 68, 0.1)',
                border: '1px solid #ef4444',
                borderRadius: '4px',
                fontSize: '12px',
                color: '#ef4444'
              }}>
                {validateAreaOfInterest(sceneOptions.areaOfInterest).map((error, index) => (
                  <div key={index}>{error}</div>
                ))}
              </div>
            )}
          </div>

          {/* Target Configuration */}
          <div style={{ marginBottom: '20px' }}>
            <h5 style={{ marginTop: 0, marginBottom: '10px' }}>Target Configuration</h5>
            <div style={inputGroupStyle}>
              <label htmlFor="targetShape">Shape:</label>
              <select
                id="targetShape"
                value={targetConfig.shape}
                onChange={(e) => setSceneOptions(opts => ({ targetConfig: { ...opts.targetConfig, shape: e.target.value } }))}
                style={inputStyle}
              >
                <option value="cuboid">Cuboid</option>
                <option value="cylinder">Cylinder</option>
              </select>
            </div>
            {targetConfig.shape === 'cuboid' ? (
              <>
                <div style={inputGroupStyle}>
                  <label htmlFor="targetWidth">Width (X) (m):</label>
                  <input
                    id="targetWidth"
                    type="number"
                    step="0.001"
                    value={formatInputNumber(targetConfig.width)}
                    onChange={(e) => setSceneOptions(opts => ({ targetConfig: { ...opts.targetConfig, width: Number(e.target.value) } }))}
                    style={inputStyle}
                  />
                </div>
                <div style={inputGroupStyle}>
                  <label htmlFor="targetDepth">Depth (Y) (m):</label>
                  <input
                    id="targetDepth"
                    type="number"
                    step="0.001"
                    value={formatInputNumber(targetConfig.depth)}
                    onChange={(e) => setSceneOptions(opts => ({ targetConfig: { ...opts.targetConfig, depth: Number(e.target.value) } }))}
                    style={inputStyle}
                  />
                </div>
              </>
            ) : (
              <div style={inputGroupStyle}>
                <label htmlFor="targetDiameter">Diameter (m):</label>
                <input
                  id="targetDiameter"
                  type="number"
                  step="0.001"
                  value={formatInputNumber(targetConfig.width)}
                  onChange={(e) => {
                    const diameter = Number(e.target.value);
                    setSceneOptions(opts => ({ targetConfig: { ...opts.targetConfig, width: diameter, depth: diameter } }))
                  }}
                  style={inputStyle}
                />
              </div>
            )}
            <div style={inputGroupStyle}>
              <label htmlFor="targetHeight">Height (Z) (m):</label>
              <input
                id="targetHeight"
                type="number"
                step="0.001"
                value={formatInputNumber(targetConfig.height)}
                onChange={(e) => setSceneOptions(opts => ({ targetConfig: { ...opts.targetConfig, height: Number(e.target.value) } }))}
                style={inputStyle}
              />
            </div>

            <div style={inputGroupStyle}>
              <label htmlFor="groundOffset">Ground Offset (m):</label>
              <input
                id="groundOffset"
                type="number"
                step="0.001"
                value={formatInputNumber(targetConfig.groundOffset)}
                onChange={(e) => setSceneOptions(opts => ({ targetConfig: { ...opts.targetConfig, groundOffset: Number(e.target.value) } }))}
                style={inputStyle}
              />
            </div>
          </div>

          {/* Heatmap Generation */}
          <div style={{ marginBottom: '20px' }}>

            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                onClick={handleGenerateHeatmap}
                disabled={isLoading || !sensors || sensors.length === 0 || !isValidAreaOfInterest(sceneOptions.areaOfInterest)}
                style={{
                  ...buttonStyle,
                  width: 'auto',
                  flex: 1,
                  opacity: (!sensors || sensors.length === 0 || !isValidAreaOfInterest(sceneOptions.areaOfInterest)) ? 0.5 : 1
                }}
                title={
                  !sensors || sensors.length === 0
                    ? 'Add at least one sensor to generate a heatmap'
                    : !isValidAreaOfInterest(sceneOptions.areaOfInterest)
                      ? `Invalid grid parameters: ${validateAreaOfInterest(sceneOptions.areaOfInterest).join(', ')}`
                      : ''
                }
              >
                {isLoading ? 'Generating...' : 'Generate Heatmap'}
              </button>
              {isLoading && (
                <button onClick={handleCancel} style={{ ...buttonStyle, width: 'auto', flex: 1, background: '#ef4444' }}>
                  Cancel
                </button>
              )}
            </div>

            {/* Progress Bar */}
            {isLoading && (
              <div style={{ marginTop: '10px' }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '5px',
                  fontSize: '12px',
                  color: '#666'
                }}>
                  <span>{statusMessage}</span>
                  <span>{progress}%</span>
                </div>
                <div style={{
                  width: '100%',
                  height: '8px',
                  backgroundColor: '#e5e7eb',
                  borderRadius: '4px',
                  overflow: 'hidden'
                }}>
                  <div style={{
                    width: `${progress}%`,
                    height: '100%',
                    backgroundColor: '#3b82f6',
                    transition: 'width 0.3s ease',
                    borderRadius: '4px'
                  }} />
                </div>
              </div>
            )}
          </div>

          {/* Heatmap Display */}
          {heatmapData && (
            <div style={{ marginBottom: '20px' }}>

              <div style={{ display: 'flex', gap: '10px' }}>
                <button onClick={() => setHeatmapVisible(!heatmapVisible)} style={{ ...buttonStyle, width: 'auto', flex: 1 }}>
                  {heatmapVisible ? 'Hide Heatmap' : 'Show Heatmap'}
                </button>
              </div>
            </div>
          )}

          {/* Heatmap Legend */}
          {heatmapVisible && (
            <div style={{ marginBottom: '20px' }}>
              <h5 style={{ marginTop: 0, marginBottom: '10px', textAlign: 'center' }}>Heatmap Legend</h5>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '12px' }}>
                <input
                  type="number"
                  value={formatInputNumber(sceneOptions.heatmapScale.min)}
                  onChange={(e) => setSceneOptions(opts => ({ heatmapScale: { ...opts.heatmapScale, min: Number(e.target.value) } }))}
                  style={{ ...inputStyle, width: '60px' }}
                />
                <div className="color-scale-dropdown" style={{ position: 'relative', flex: 1, margin: '0 10px' }}>
                  <div
                    style={{
                      height: '20px',
                      background: generateGradient(sceneOptions.colorScale),
                      borderRadius: '4px',
                      cursor: 'pointer',
                      border: '1px solid #374151',
                      position: 'relative'
                    }}
                    onClick={() => setShowColorScaleDropdown(!showColorScaleDropdown)}
                    title="Click to change color scale"
                  />
                  {showColorScaleDropdown && (
                    <div
                      className="color-scale-dropdown"
                      style={{
                        position: 'absolute',
                        top: '25px',
                        left: '0',
                        right: '0',
                        background: '#1f2937',
                        border: '1px solid #374151',
                        borderRadius: '4px',
                        zIndex: 1000,
                        padding: '5px 0'
                      }}
                    >
                      {[
                        { value: 'blue-red', label: 'Blue-Red' },
                        { value: 'jet', label: 'Jet (Default)' },
                        { value: 'viridis', label: 'Viridis' },
                        { value: 'plasma', label: 'Plasma' },
                        { value: 'inferno', label: 'Inferno' },
                        { value: 'magma', label: 'Magma' },
                        { value: 'turbo', label: 'Turbo' }
                      ].map((scale) => (
                        <div
                          key={scale.value}
                          style={{
                            padding: '8px 12px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            fontSize: '12px',
                            background: sceneOptions.colorScale === scale.value ? '#374151' : 'transparent'
                          }}
                          onClick={() => {
                            setSceneOptions({ colorScale: scale.value as ColorScale });
                            setShowColorScaleDropdown(false);
                          }}
                        >
                          <div
                            style={{
                              width: '20px',
                              height: '12px',
                              background: generateGradient(scale.value as ColorScale),
                              borderRadius: '2px',
                              border: '1px solid #6b7280'
                            }}
                          />
                          <span>{scale.label}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <input
                  type="number"
                  value={formatInputNumber(sceneOptions.heatmapScale.max)}
                  onChange={(e) => setSceneOptions(opts => ({ heatmapScale: { ...opts.heatmapScale, max: Number(e.target.value) } }))}
                  style={{ ...inputStyle, width: '60px' }}
                />
              </div>
            </div>
          )}

          {/* Image Export */}
          <div style={{ marginBottom: '10px' }}>
            <button
              onClick={handleExportImage}
              disabled={!canExport || isExporting}
              style={{
                ...buttonStyle,
                opacity: (!canExport || isExporting) ? 0.5 : 1,
                cursor: (!canExport || isExporting) ? 'not-allowed' : 'pointer',
              }}
              title={
                !canExport
                  ? 'Scene is not ready for export'
                  : 'Export the current view as a PNG with scale bar, heatmap legend (when shown), and target configuration'
              }
            >
              {isExporting ? 'Exporting...' : 'Export Image'}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default SimulationPanel;